import { PieChart, Package, TrendingUp, CheckCircle2, Clock, AlertCircle, BarChart3 } from 'lucide-react';
import { Modal } from '@components/ui';
import { MATERIAL_CATEGORIES, calculateEstimatedCost, getCostByCategory } from '@mocks/malzemeler';
import clsx from 'clsx';

export default function CostSummary({ isOpen, onClose, materials, stats, formatCurrency }) {
    const categoryBreakdown = getCostByCategory(materials);

    // En yüksek maliyetli 5 malzeme
    const topMaterials = [...materials]
        .sort((a, b) => calculateEstimatedCost(b) - calculateEstimatedCost(a))
        .slice(0, 5);

    // Kategori yüzdelerini hesapla
    const categoryPercentages = Object.entries(categoryBreakdown).map(([key, value]) => ({
        key,
        label: MATERIAL_CATEGORIES[key]?.label || key,
        color: MATERIAL_CATEGORIES[key]?.color || 'neutral',
        value,
        percentage: stats.totalEstimatedCost > 0 ? ((value / stats.totalEstimatedCost) * 100).toFixed(1) : 0,
    })).sort((a, b) => b.value - a.value);

    const formatCompact = (amount) => {
        if (Math.abs(amount) >= 1000000) return `${(amount / 1000000).toFixed(1)}M ₺`;
        if (Math.abs(amount) >= 1000) return `${Math.round(amount / 1000)}K ₺`;
        return formatCurrency(amount);
    };

    const budgetProgress = stats.totalEstimatedCost > 0
        ? Math.min(100, (stats.totalActualCost / stats.totalEstimatedCost) * 100)
        : 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" title="📊 Maliyet Özeti"
            footer={
                <button onClick={onClose}
                    className="w-full sm:w-auto py-3 px-6 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors min-h-[48px]">
                    Kapat
                </button>
            }>
            <div className="space-y-6">
                {/* Toplam Maliyet + Progress */}
                <div className="bg-primary/5 rounded-xl p-5 text-center">
                    <p className="text-text-secondary mb-1">Toplam Tahmini Maliyet</p>
                    <p className="text-3xl sm:text-4xl font-bold text-primary">{formatCurrency(stats.totalEstimatedCost)}</p>
                    <p className="text-sm text-text-muted mt-1">{stats.totalMaterials} kalem malzeme</p>

                    {/* Bütçe barı */}
                    <div className="mt-4 max-w-xs mx-auto">
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-text-muted">Gerçekleşen</span>
                            <span className="font-semibold text-text-primary">{formatCompact(stats.totalActualCost)} (%{budgetProgress.toFixed(1)})</span>
                        </div>
                        <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                            <div className={clsx("h-full rounded-full transition-all",
                                budgetProgress > 90 ? 'bg-error' : budgetProgress > 70 ? 'bg-warning' : 'bg-success'
                            )} style={{ width: `${budgetProgress}%` }} />
                        </div>
                    </div>
                </div>

                {/* Durum Kartları */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-success/5 rounded-xl text-center border border-success/20">
                        <CheckCircle2 size={18} className="text-success mx-auto mb-1" />
                        <p className="text-lg font-bold text-text-primary">{stats.completedItems}</p>
                        <p className="text-xs text-text-muted">Tamamlanan</p>
                    </div>
                    <div className="p-3 bg-warning/5 rounded-xl text-center border border-warning/20">
                        <Clock size={18} className="text-warning mx-auto mb-1" />
                        <p className="text-lg font-bold text-text-primary">{stats.inProgressItems}</p>
                        <p className="text-xs text-text-muted">Devam Eden</p>
                    </div>
                    <div className="p-3 bg-bg-tertiary rounded-xl text-center border border-border-light">
                        <AlertCircle size={18} className="text-text-muted mx-auto mb-1" />
                        <p className="text-lg font-bold text-text-primary">{stats.pendingItems}</p>
                        <p className="text-xs text-text-muted">Bekleyen</p>
                    </div>
                </div>

                {/* Kategori Dağılımı */}
                <div>
                    <h3 className="text-base font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <PieChart size={16} />Kategori Dağılımı
                    </h3>
                    <div className="space-y-3">
                        {categoryPercentages.map((cat) => (
                            <div key={cat.key}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-text-secondary">{cat.label}</span>
                                    <div className="text-right">
                                        <span className="text-sm font-medium text-text-primary">{formatCurrency(cat.value)}</span>
                                        <span className="text-xs text-text-muted ml-2">(%{cat.percentage})</span>
                                    </div>
                                </div>
                                <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all bg-primary" style={{ width: `${cat.percentage}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* En Yüksek Maliyetli Kalemler */}
                <div>
                    <h3 className="text-base font-semibold text-text-primary mb-3 flex items-center gap-2">
                        <TrendingUp size={16} />En Yüksek Maliyetli 5 Kalem
                    </h3>
                    <div className="space-y-2">
                        {topMaterials.map((material, index) => (
                            <div key={material.id} className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">
                                        {index + 1}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-text-primary truncate">{material.name}</p>
                                        <p className="text-xs text-text-muted">{MATERIAL_CATEGORIES[material.category]?.label}</p>
                                    </div>
                                </div>
                                <span className="text-sm font-bold text-primary shrink-0 ml-2">
                                    {formatCurrency(calculateEstimatedCost(material))}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
